import { motion } from "motion/react";

import { useMicrophoneVolume } from "../hooks/useMicrophoneVolume";

interface AudioVisualizerProps {
  className?: string;
  barCount?: number;
  height?: string;
}

export default function AudioVisualizer({
  className = "",
  barCount = 5,
  height = "h-6",
}: AudioVisualizerProps) {
  const { volume, isSpeaking } = useMicrophoneVolume();

  const getBarHeight = (index: number) => {
    if (!isSpeaking) return "h-1";

    // Calculate bar height based on volume and position
    const baseHeight = Math.max(0.1, volume);
    const positionFactor = 1 - (index / barCount) * 0.5; // Bars get shorter towards the end
    const heightMultiplier = baseHeight * positionFactor;

    // Map to height classes
    if (heightMultiplier > 0.8) return "h-full";
    if (heightMultiplier > 0.6) return "h-4";
    if (heightMultiplier > 0.4) return "h-3";
    if (heightMultiplier > 0.2) return "h-2";

    return "h-1";
  };

  const getBarColor = (index: number) => {
    if (!isSpeaking) return "bg-accent-foreground/30";

    const intensity = volume * (1.5 - index / barCount);

    if (intensity > 0.7) return "bg-red-500";
    if (intensity > 0.4) return "bg-yellow-500";
    if (intensity > 0.1) return "bg-green-500";

    return "bg-accent-foreground/75";
  };

  return (
    <div className={`flex w-full items-end gap-0.5 ${height} ${className}`}>
      {Array.from({ length: barCount }).map((_, index) => (
        <motion.div
          key={index}
          animate={{
            height: isSpeaking ? "100%" : "25%",
            opacity: isSpeaking ? 1 : 0.5,
            width: "100%",
          }}
          className={`w-1 rounded-full transition-all duration-150 ${getBarColor(index)} ${getBarHeight(index)}`}
          transition={{
            duration: 0.1,
            delay: index * 0.01,
          }}
        />
      ))}
    </div>
  );
}
